// Bridge to the Nexo Meet desktop app (Electron, desktop/). Present only when this client runs
// inside the app; in a normal browser `desktop` is null and nothing here is used.

export interface DesktopSource {
  id: string;
  name: string;
  kind: 'screen' | 'window';
  /** JPEG data URL of what the screen/window shows right now ('' when the OS gave none). */
  thumbnail: string;
  icon: string | null;
}

export type ScreenPermission = 'granted' | 'denied' | 'not-determined' | 'restricted' | 'unknown';

export interface DesktopInfo {
  version: string;
  platform: 'darwin' | 'win32' | 'linux';
  osVersion: string;
  /** System sound can be shared (Windows, macOS 13+). */
  systemAudio: boolean;
}

export interface DesktopBridge {
  platform: 'darwin' | 'win32' | 'linux';
  info(): Promise<DesktopInfo>;
  listSources(): Promise<{ permission: ScreenPermission; sources: DesktopSource[] }>;
  /** Tells the app which source the next getDisplayMedia call gets. */
  chooseSource(id: string, audio: boolean, name: string): Promise<void>;
  openScreenSettings(): Promise<void>;
  mainWindow(action: 'minimize' | 'restore'): Promise<void>;
  /** Sizes a floating window (nexo-bar / nexo-cameras) to its contents. */
  fitFloating(name: 'nexo-bar' | 'nexo-cameras', width: number, height: number): Promise<void>;
  changeServer(): Promise<void>;
}

declare global {
  interface Window {
    nexoDesktop?: DesktopBridge;
  }
}

export const desktop: DesktopBridge | null = typeof window !== 'undefined' ? (window.nexoDesktop ?? null) : null;

export function isDesktopApp(): boolean {
  return desktop !== null;
}
